import { convertNumberToCurrency, convertScaledValueToDecimal } from '../../helpers/number';
import { generateUuid } from '../../helpers/string';
import trans from '../../helpers/trans';
import { MeilisearchDiscount } from '../@api/Meilisearch';
import {
    DiscountBaseResource,
    DiscountCodeWarnings,
    DiscountResource,
    PricesResource,
} from '../@api/Sylius';
import { Discount, ProductPriceInterface } from './Price';

const transformDiscountBaseToDiscount = (resource: DiscountBaseResource): Discount => {
    const id = generateUuid();

    return {
        id,
        isPercentage: resource.isPercentage,
        label: resource.label,
        value: resource.value,
    };
};

export const transformToProductPrice = (price: number, originalPrice: number, discountBase?: DiscountBaseResource): ProductPriceInterface => {
    const hasDiscount = originalPrice > price;

    const discount = hasDiscount && discountBase
        ? transformDiscountBaseToDiscount(discountBase)
        : undefined;

    const discountInPercentage = (hasDiscount && discountBase?.isPercentage)
        ? convertScaledValueToDecimal(discountBase.value)
        : undefined;

    const roundedDiscountInPercentage = discountInPercentage
        ? Math.round(discountInPercentage)
        : undefined;

    const discountInPrice = hasDiscount
        ? convertScaledValueToDecimal(originalPrice - price)
        : undefined;

    return {
        original: convertScaledValueToDecimal(originalPrice),
        current: convertScaledValueToDecimal(price),
        discount,
        discountInPercentage: roundedDiscountInPercentage,
        discountInPrice,
    };
};

export const transformMeilisearchDiscountToDiscount = (resource: MeilisearchDiscount): Discount => ({
    id: generateUuid(),
    value: resource.value,
    label: resource.label,
    isPercentage: resource.is_percentage,
});

export const transformToDiscount = (resource: DiscountResource, warnings?: DiscountCodeWarnings): Discount => {
    const id = resource.erpId.toString();

    const discountValue = resource.isPercentage
        ? `${convertScaledValueToDecimal(resource.value)}%`
        : convertNumberToCurrency(resource.value, {
            includeCurrencySymbol: true,
            isCents: true,
        });

    const label = trans('entities.price.discount', {
        name: `${resource.label} (${discountValue})`,
    });

    const value = resource.isPercentage
        ? resource.value
        : Math.abs(resource.actualEffect);

    const invalid = resource.isOrderLevel && warnings
        ? warnings.discountCodeHasNoEffect || warnings.discountValueNotFullyUsed
        : false;

    return {
        id,
        isPercentage: resource.isPercentage,
        label,
        value,
        productDiscount: !resource.isOrderLevel,
        invalid,
    };
};

export const calculateDiscounts = (resource: PricesResource): Discount[] => {
    const discounts: Discount[] = [];

    let productDiscounts: DiscountResource[] = [];
    let orderLevelDiscounts: DiscountResource[] = [];

    if (resource.discounts) {
        productDiscounts = resource.discounts.filter(discount => !discount.isOrderLevel);
        orderLevelDiscounts = resource.discounts.filter(discount => discount.isOrderLevel);
    }

    const productDiscountsTotal = productDiscounts.reduce((total, discount) => (
        total + discount.actualEffect
    ), 0);

    if (productDiscountsTotal !== 0) {
        discounts.push({
            id: 'product-discounts',
            productDiscount: true,
            isPercentage: false,
            label: trans('entities.price.productDiscountsTotal'),
            value: Math.abs(productDiscountsTotal),
        });
    }

    orderLevelDiscounts.forEach(discount => {
        const item = transformToDiscount(discount, resource?.warnings || {});

        discounts.push(item);
    });

    return discounts;
};
